/**
 * useMedicalChatbot Hook
 * ======================
 * React hook for managing medical chatbot interactions
 *
 * Features:
 * - Natural language queries about patient history
 * - Suggested questions generation
 * - Conversation history management
 * - Error handling and loading states
 *
 * Based on: CONVERSATIONAL_NOTES_ENHANCEMENTS_PLAN.md
 *
 * Author: Claude Code
 * Date: 2026-01-14
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSenseiChatContext, getSenseiChatKey } from '../contexts/SenseiChatContext';

// ==========================================
// TYPES
// ==========================================

export interface ChatMessage {
  question: string;
  answer: string;
  generated_at: string;
  sources: string[];
  confidence: string;
}

interface UseMedicalChatbotOptions {
  patientId?: string;
  sessionId?: string;
  autoLoadSuggestions?: boolean; // default true
}

interface UseMedicalChatbotReturn {
  // Conversation
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  clearMessages: () => void;

  // Query
  query: (question: string) => Promise<void>;
  isQuerying: boolean;
  queryError: string | null;

  // Suggested questions
  suggestedQuestions: string[];
  loadSuggestedQuestions: () => Promise<void>;
  suggestionsLoading: boolean;
  suggestionsError: string | null;

  // Controls
  currentQuestion: string;
  setCurrentQuestion: (question: string) => void;
}

// ==========================================
// HOOK IMPLEMENTATION
// ==========================================

export const useMedicalChatbot = (
  options: UseMedicalChatbotOptions = {}
): UseMedicalChatbotReturn => {
  const {
    patientId,
    sessionId,
    autoLoadSuggestions = true,
  } = options;

  const ctx = useSenseiChatContext();
  const key = getSenseiChatKey(patientId, sessionId);
  const usePersisted = Boolean(ctx && key);

  // Refs so loadSuggestedQuestions doesn't depend on ctx/key (avoids effect loop when context updates)
  const ctxRef = useRef(ctx);
  const keyRef = useRef(key);
  ctxRef.current = ctx;
  keyRef.current = key;

  // ==========================================
  // STATE (local fallback when no context)
  // ==========================================

  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');

  const [isQuerying, setIsQuerying] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);

  const [localSuggestedQuestions, setLocalSuggestedQuestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  const messages = usePersisted ? (ctx!.getSlot(key!).messages) : localMessages;
  // Prefer context when it has suggestions; else show local (so quick prompts appear after first load)
  const slotSuggestions = usePersisted ? ctx!.getSlot(key!).suggestedQuestions : [];
  const suggestedQuestions =
    usePersisted && slotSuggestions.length > 0 ? slotSuggestions : localSuggestedQuestions;

  // ==========================================
  // CONVERSATION MANAGEMENT
  // ==========================================

  const addMessage = useCallback((message: ChatMessage) => {
    if (usePersisted && key && ctx) {
      ctx.addMessage(key, message);
    } else {
      setLocalMessages((prev) => [...prev, message]);
    }
    console.log('[useMedicalChatbot] Message added:', {
      question: message.question,
      sources: message.sources.length,
      confidence: message.confidence,
    });
  }, [usePersisted, key, ctx]);

  const clearMessages = useCallback(() => {
    if (usePersisted && key && ctx) {
      ctx.clearMessages(key);
    } else {
      setLocalMessages([]);
    }
    console.log('[useMedicalChatbot] Conversation cleared');
  }, [usePersisted, key, ctx]);

  // ==========================================
  // API CALLS
  // ==========================================

  /**
   * Query the medical chatbot
   */
  const query = useCallback(async (question: string) => {
    if (!patientId) {
      console.warn('[useMedicalChatbot] No patientId provided for query');
      setQueryError('No patient selected');
      return;
    }

    if (!question.trim()) {
      console.warn('[useMedicalChatbot] Empty question');
      setQueryError('Question cannot be empty');
      return;
    }

    setIsQuerying(true);
    setQueryError(null);

    try {
      // Build conversation history for context
      const conversationHistory = messages.map((msg) => ({
        question: msg.question,
        answer: msg.answer,
      }));

      const response = await fetch('/api/medical-chatbot/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_id: patientId,
          question: question.trim(),
          session_id: sessionId,
          conversation_history: conversationHistory.length > 0 ? conversationHistory : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Query failed: ${response.status}`);
      }

      const data: ChatMessage = await response.json();

      // Add to conversation
      addMessage(data);

      // Clear input
      setCurrentQuestion('');

      console.log('[useMedicalChatbot] Query successful:', {
        question: data.question,
        confidence: data.confidence,
        sources: data.sources.length,
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[useMedicalChatbot] Query error:', errorMessage);
      setQueryError(errorMessage);

    } finally {
      setIsQuerying(false);
    }
  }, [patientId, sessionId, messages, addMessage]);

  /**
   * Load suggested questions
   */
  const loadSuggestedQuestions = useCallback(async () => {
    if (!patientId) {
      console.warn('[useMedicalChatbot] No patientId provided for suggestions');
      return;
    }

    setSuggestionsLoading(true);
    setSuggestionsError(null);

    try {
      const url = sessionId
        ? `/api/medical-chatbot/suggested-questions/${patientId}?session_id=${sessionId}`
        : `/api/medical-chatbot/suggested-questions/${patientId}`;

      const response = await fetch(url);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to load suggestions: ${response.status}`);
      }

      const data = await response.json();
      const suggestions = data.suggestions || [];
      setLocalSuggestedQuestions(suggestions);
      const c = ctxRef.current;
      const k = keyRef.current;
      if (c && k) c.setSuggestedQuestions(k, suggestions);

      console.log(`[useMedicalChatbot] Loaded ${suggestions.length} suggested questions`);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[useMedicalChatbot] Suggestions error:', errorMessage);
      setSuggestionsError(errorMessage);
      setLocalSuggestedQuestions([]);
      const c = ctxRef.current;
      const k = keyRef.current;
      if (c && k) c.setSuggestedQuestions(k, []);

    } finally {
      setSuggestionsLoading(false);
    }
  }, [patientId, sessionId]);

  // ==========================================
  // EFFECTS
  // ==========================================

  /**
   * Load suggested questions when patientId changes
   */
  useEffect(() => {
    if (patientId && autoLoadSuggestions) {
      loadSuggestedQuestions();
    }
  }, [patientId, autoLoadSuggestions, loadSuggestedQuestions]);

  /**
   * When not using persisted context, clear local messages when patient (or session) changes.
   * When using context, each key keeps its own messages across tab switches.
   */
  const prevKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (usePersisted) return;
    if (key !== prevKeyRef.current) {
      prevKeyRef.current = key;
      if (key) setLocalMessages([]);
    }
  }, [key, usePersisted]);

  // ==========================================
  // RETURN
  // ==========================================

  return {
    // Conversation
    messages,
    addMessage,
    clearMessages,

    // Query
    query,
    isQuerying,
    queryError,

    // Suggested questions
    suggestedQuestions,
    loadSuggestedQuestions,
    suggestionsLoading,
    suggestionsError,

    // Controls
    currentQuestion,
    setCurrentQuestion,
  };
};

// ==========================================
// USAGE EXAMPLE
// ==========================================

/**
 * USAGE IN COMPONENT:
 *
 * import { useMedicalChatbot } from '@/hooks/useMedicalChatbot';
 *
 * function MedicalChatbot({ patientId, sessionId }) {
 *   const {
 *     messages,
 *     query,
 *     isQuerying,
 *     queryError,
 *     suggestedQuestions,
 *     currentQuestion,
 *     setCurrentQuestion,
 *   } = useMedicalChatbot({
 *     patientId,
 *     sessionId,
 *     autoLoadSuggestions: true,
 *   });
 *
 *   const handleSubmit = async (e: React.FormEvent) => {
 *     e.preventDefault();
 *     await query(currentQuestion);
 *   };
 *
 *   const handleSuggestionClick = (suggestion: string) => {
 *     setCurrentQuestion(suggestion);
 *     query(suggestion);
 *   };
 *
 *   return (
 *     <div>
 *       {/* Suggested Questions *\/}
 *       <div>
 *         {suggestedQuestions.map((q, i) => (
 *           <button key={i} onClick={() => handleSuggestionClick(q)}>
 *             {q}
 *           </button>
 *         ))}
 *       </div>
 *
 *       {/* Conversation *\/}
 *       <div>
 *         {messages.map((msg, i) => (
 *           <div key={i}>
 *             <p><strong>Q:</strong> {msg.question}</p>
 *             <p><strong>A:</strong> {msg.answer}</p>
 *             <small>Sources: {msg.sources.join(', ')}</small>
 *           </div>
 *         ))}
 *       </div>
 *
 *       {/* Input *\/}
 *       <form onSubmit={handleSubmit}>
 *         <input
 *           value={currentQuestion}
 *           onChange={(e) => setCurrentQuestion(e.target.value)}
 *           placeholder="Ask a question..."
 *           disabled={isQuerying}
 *         />
 *         <button type="submit" disabled={isQuerying || !currentQuestion}>
 *           {isQuerying ? 'Thinking...' : 'Ask'}
 *         </button>
 *       </form>
 *
 *       {queryError && <div>Error: {queryError}</div>}
 *     </div>
 *   );
 * }
 */
